import { type IntlShape } from 'react-intl-next';

import type { CreatePluginPresetsConfig } from '@atlassian/link-create-presets';
import type { CreatePayload } from '@atlaskit/link-create';
import {
	fetchDefaultValues,
	createConfluencePage,
	getURLFromConfluencePageResponse,
	ContentTypes,
} from '@atlassian/link-create-confluence';

import { confluencePageMessages } from '../../ui/messages';
import createEventPayload from '../../common/utils/analytics/generated/create-event-payload';
import type { EventKey } from '../../common/utils/analytics/generated/analytics.types';

/* Function to create a new confluence draft with a default title of
 * 'Untitled page' (localized) and by default the page is created as a
 * child of the current page.
 */
export const createConfluenceDraft = async ({
	intl,
	defaultCloudId,
	defaultFormValues,
	gatewayBaseUrl,
	onCreate,
	setError,
	fireAnalyticsEvent,
}: {
	intl: IntlShape;
	onCreate: (payload: CreatePayload) => void;
	defaultCloudId: string;
	gatewayBaseUrl: string | undefined;
	defaultFormValues: CreatePluginPresetsConfig['defaultFormValues'];
	setError: (value: Error) => void;
	fireAnalyticsEvent: <K extends EventKey>(
		...params: Parameters<typeof createEventPayload<K>>
	) => void;
}) => {
	const siteUrl = window.location.origin;

	const defaultParentId = defaultFormValues?.['confluence-page']?.parentId;
	const defaultSpaceId = defaultFormValues?.['confluence-page']?.spaceId;

	try {
		const pageInfo = await fetchDefaultValues(
			intl,
			defaultCloudId,
			gatewayBaseUrl,
			defaultSpaceId,
			defaultParentId,
		);

		const spaceId = pageInfo?.value?.spaceId;

		// if there is no space id, we cannot create the page -
		// throw an error to show the error flag
		if (!spaceId) {
			throw new Error('No spaceId found');
		}

		const pageSuccessResponse = await createConfluencePage({
			// As we are creating a draft, we always want to default
			// the title to (localized) 'Untitled page'
			title: intl.formatMessage(confluencePageMessages.newDraftTitle),
			spaceId,
			parentId: defaultParentId,
			cloudId: defaultCloudId,
			gatewayBaseUrl: gatewayBaseUrl,
			status: 'draft',
			fireAnalyticsEvent,
			creationFlow: 'inline',
		});

		const url = getURLFromConfluencePageResponse({
			data: pageSuccessResponse,
			hostname: siteUrl,
		});

		onCreate({
			url,
			objectId: pageSuccessResponse.id,
			objectType: ContentTypes.PAGE,
		});

		fireAnalyticsEvent('track.object.created', {
			source: 'inlineCreateOneStepExperiment',
			objectName: 'confluence-page', // One step create is only used for confluence pages
		});
	} catch (err) {
		setError(err as Error);
	}
};
